import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { contentPrinciples, siteMeta, stats, testimonials } from "@/mocks/leads";
import { useSeo, BASE_URL } from "@/hooks/useSeo";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const positioning = [
  {
    title: "对投稿人",
    desc: "提供一个愿意花时间核实的出口。不鼓励夸大，也不要求你自证清白，先说出来再一起补齐细节。",
    icon: "ri-user-heart-line",
  },
  {
    title: "对读者",
    desc: "提供状态透明的内容。已核实、多方印证、核实中，三种标注让你清楚知道每条信息的可靠程度。",
    icon: "ri-book-read-line",
  },
  {
    title: "对议题本身",
    desc: "做长期跟踪的耐心方。不因热点散场就停止追问，让被记录下来的问题有机会被真正解决。",
    icon: "ri-anchor-line",
  },
];

export default function About() {
  useScrollReveal();

  useSeo({
    title: "关于我们 - 蝌蚪爆料｜线索核实平台的定位与内容原则",
    description:
      "了解蝌蚪爆料：一个专注线索投稿与信息核实的平台。我们坚持只呈现可核实的事实、严格保护来源信息、给相关方回应空间、不追热点不编情节，为投稿人与读者搭建可信的对接通道。",
    keywords: "蝌蚪爆料,关于蝌蚪爆料,线索核实,内容原则,来源保护",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于蝌蚪爆料",
        url: `${BASE_URL}/about`,
        inLanguage: "zh-CN",
        description: "蝌蚪爆料的平台定位、内容原则与来源保护说明。",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "蝌蚪爆料",
        url: BASE_URL,
        email: siteMeta.email,
        foundingDate: siteMeta.established,
        description: "线索投稿与信息核实平台，为提供真实素材的投稿人与关注公共议题的读者搭建可信的对接通道。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: BASE_URL },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${BASE_URL}/about` },
        ],
      },
    ],
  });

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100 py-10 md:py-14">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-5 max-w-4xl font-heading text-3xl font-bold leading-snug text-foreground-950 md:text-5xl">
            我们想做的，是让认真核实这件事重新变得有价值
          </h1>
          <p className="mt-5 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            蝌蚪爆料成立于 {siteMeta.established} 年，是一个专注线索征集的投稿平台。
            我们把散落在亲历者手上的碎片信息收集起来，经过交叉核实，再以清晰、可判断的方式呈现给需要它的人。
          </p>
        </div>
      </section>

      <section className="w-full bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center">
            <div className="reveal lg:col-span-6">
              <div className="h-64 w-full overflow-hidden rounded-lg border border-background-200 md:h-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20editorial%20workspace%20with%20notebooks%20laminates%20and%20a%20desk%20lamp%2C%20quiet%20afternoon%20light%2C%20paper%20textures%20and%20soft%20shadows%2C%20investigative%20research%20atmosphere%2C%20muted%20amber%20and%20neutral%20tones%2C%20no%20text&width=1000&height=900&seq=kedou-about-2026-c3&orientation=landscape"
                  alt="蝌蚪爆料 核实编辑工作场景"
                  title="蝌蚪爆料 线索核实流程"
                  className="h-full w-full object-cover object-center"
                />
              </div>
            </div>
            <div className="reveal lg:col-span-6">
              <span className="text-xs font-semibold tracking-[0.25em] text-primary-600">BRAND STORY</span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                从一个简单的判断开始
              </h2>
              <div className="mt-4 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                <p>
                  我们在整理第一手信息时发现，很多真正有价值的细节，往往掌握在当事人或从业者手上，
                  但他们缺少一个既安全、又愿意花时间核实的出口。另一方面，读者面对真假混杂的信息，
                  也很难判断哪些值得相信。
                </p>
                <p>
                  蝌蚪爆料想解决的正是这个错位：为愿意说的人提供一个可以放心开口的地方，
                  为想看的人提供一套诚实的信息标注方式。我们不替任何人下结论，只把可核实的事实整理清楚。
                </p>
                <p>
                  「蝌蚪」取的是小而真实的意思——它不显眼，但每一条都能追溯到它从哪里来。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-foreground-950 py-12 md:py-14">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <dl className="grid grid-cols-2 gap-8 lg:grid-cols-4">
            {stats.map((s) => (
              <div key={s.label} className="reveal">
                <dt className="text-xs text-background-400 md:text-sm">{s.label}</dt>
                <dd className="mt-2 font-heading text-2xl font-bold text-background-50 md:text-4xl">
                  {s.value}
                  <span className="ml-1 text-sm font-normal text-background-400">{s.unit}</span>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="w-full bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="reveal max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.25em] text-primary-600">POSITIONING</span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              平台定位：三方之间的那条通道
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
              我们既不是裁判，也不是传声筒，而是一条尽量保持中立的通道，让信息在传递过程中不失真。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            {positioning.map((p) => (
              <article
                key={p.title}
                className="reveal flex flex-col rounded-lg border border-background-200 bg-background-50 p-6"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className={`${p.icon} text-2xl`} aria-hidden="true" />
                </span>
                <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 py-14 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="reveal max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.25em] text-primary-600">PRINCIPLES</span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              内容原则：我们如何做出取舍
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-x-10 gap-y-6 md:grid-cols-2">
            {contentPrinciples.map((p, i) => (
              <div key={p.title} className="reveal flex gap-5 border-t border-background-200 pt-6">
                <span className="font-heading text-xl font-bold text-primary-500">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-secondary-50 py-14 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="reveal max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.25em] text-secondary-700">REPUTATION</span>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              投稿人与读者怎么说
            </h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure
                key={t.name}
                className="reveal flex flex-col rounded-lg border border-secondary-200 bg-background-50 p-6"
              >
                <div className="flex items-center gap-1 text-accent-500">
                  {[0, 1, 2, 3, 4].map((n) => (
                    <i key={n} className="ri-star-fill text-sm" aria-hidden="true" />
                  ))}
                </div>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {t.text}
                </blockquote>
                <figcaption className="mt-5 border-t border-background-200 pt-4">
                  <p className="font-heading text-sm font-semibold text-foreground-950">{t.name}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{t.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100 py-14 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200 bg-background-50 p-6 md:p-10 lg:grid-cols-12 lg:items-center">
            <div className="reveal lg:col-span-7">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                联系我们
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                无论是提供线索、补充材料，还是希望与我们讨论内容核实方式，都欢迎通过下面的方式联系。
              </p>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-md bg-background-100 p-4">
                  <p className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-mail-line text-primary-500" aria-hidden="true" />
                    投稿邮箱
                  </p>
                  <p className="mt-1.5 font-heading text-sm font-semibold text-foreground-950">
                    {siteMeta.email}
                  </p>
                </div>
                <div className="rounded-md bg-background-100 p-4">
                  <p className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-phone-line text-primary-500" aria-hidden="true" />
                    线索热线
                  </p>
                  <p className="mt-1.5 font-heading text-sm font-semibold text-foreground-950">
                    {siteMeta.hotline}
                  </p>
                </div>
                <div className="rounded-md bg-background-100 p-4">
                  <p className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-wechat-line text-primary-500" aria-hidden="true" />
                    微信对接
                  </p>
                  <p className="mt-1.5 font-heading text-sm font-semibold text-foreground-950">
                    {siteMeta.wechat}
                  </p>
                </div>
                <div className="rounded-md bg-background-100 p-4">
                  <p className="flex items-center gap-2 text-xs text-foreground-500">
                    <i className="ri-time-line text-primary-500" aria-hidden="true" />
                    受理时间
                  </p>
                  <p className="mt-1.5 font-heading text-sm font-semibold text-foreground-950">
                    每日 09:00 - 22:00
                  </p>
                </div>
              </div>
            </div>
            <div className="reveal lg:col-span-5">
              <div className="rounded-lg bg-foreground-950 p-6 text-background-100">
                <h3 className="font-heading text-lg font-bold text-background-50">准备好投稿了吗？</h3>
                <p className="mt-2 text-sm leading-relaxed text-background-400">
                  不需要写得很完整，把你知道的部分先写下来。核实编辑会与你一起补齐剩下的细节。
                </p>
                <div className="mt-6 flex flex-col gap-3 sm:flex-row lg:flex-col">
                  <Link
                    to="/guide#submit"
                    className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-send-plane-2-line" aria-hidden="true" />
                    前往投稿入口
                  </Link>
                  <Link
                    to="/leads"
                    className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/25 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                  >
                    浏览线索库
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}